import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

export const LibraryItemTypeSchema = z.enum([
  'source',
  'medical-device',
  'research-paper',
]);

export type LibraryItemType = z.infer<typeof LibraryItemTypeSchema>;

export const LibraryItemSchema = z.object({
  itemType: LibraryItemTypeSchema,
  itemId: PrimaryKeySchema,
  name: z.string(),
  collection: z.enum(['public', 'private']),
});

export type LibraryItem = z.infer<typeof LibraryItemSchema>;

export const LibraryItemsQuerySchema = z
  .object({
    itemTypes: commaDelimitedList(LibraryItemTypeSchema.array()).optional(),
    itemNameLike: z.string().optional(),

    // Source Filters
    sourceIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    sourceUrlLike: z.string().optional(),
    sourceNameLike: z.string().optional(),
    sourceIsProcessed: z.coerce.boolean().optional(),

    // Medical Device Filters
    medicalDeviceIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    medicalDeviceNameLike: z.string().optional(),

    // Research Paper Filters
    researchPaperIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    researchPaperNameLike: z.string().optional(),
  })
  .merge(createOffsetPaginationRequestQuerySchema(z.enum(['itemId'])));

export type LibraryItemsQuery = z.infer<typeof LibraryItemsQuerySchema>;

export const LibraryItemsSchema = z.array(LibraryItemSchema);

export type LibraryItems = z.infer<typeof LibraryItemsSchema>;

export const LibraryItemsSearchQuerySchema = z.object({
  limit: z.number().optional(),
  query: z.string(),
  itemTypes: commaDelimitedList(LibraryItemTypeSchema.array()).optional(),
  sourcesExcludeSourceIds: commaDelimitedList(
    PrimaryKeySchema.array(),
  ).optional(),
  sourceHasTextChunks: z.boolean().optional(),
});

export type LibraryItemsSearchQuery = z.infer<
  typeof LibraryItemsSearchQuerySchema
>;

export const LibraryItemSearchResultSchema = z.object({
  itemType: LibraryItemTypeSchema,
  itemId: PrimaryKeySchema,
  name: z.string(),
  score: z.number(),
});

export type LibraryItemSearchResult = z.infer<
  typeof LibraryItemSearchResultSchema
>;

export const LibraryItemSearchResultsSchema = z.array(
  LibraryItemSearchResultSchema,
);

export type LibraryItemSearchResults = z.infer<
  typeof LibraryItemSearchResultsSchema
>;
